import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck, CheckCircle, X, Info, ArrowRight, Clock,
  ArrowsClockwise, Envelope, ArrowCounterClockwise, PaperPlaneTilt,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatLocal } from "@/lib/timezone";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SafeHtml } from "@/lib/safeHtml";

const STATUS_TABS = [
  { id: "pending",       label: "Pending decision" },
  { id: "booked",        label: "Confirmed booked" },
  { id: "not_booked",    label: "Resumed in drip" },
  { id: "auto_resumed",  label: "Auto-resumed" },
  { id: "inquiry_sent",  label: "Inquiry sent" },
];

// Compact pill that explains every decision state at a glance.
const STATUS_BADGE = {
  pending:      { cls: "bg-[#EFF6FF] text-[#0055FF] border-[#BFDBFE]", label: "Pending" },
  booked:       { cls: "bg-[#F0FDF4] text-[#15803D] border-[#BBF7D0]", label: "Booked"  },
  not_booked:   { cls: "bg-[#FFFBEB] text-[#D97706] border-[#FDE68A]", label: "Resumed" },
  auto_resumed: { cls: "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]", label: "Auto-resumed" },
};

function HowItWorksCard() {
  // Always-visible explainer at the top of the page. The user
  // explicitly asked for on-screen instructions so a tooltip or
  // collapsed accordion isn't enough — the steps stay in front of the
  // rep until they internalise the workflow.
  return (
    <div className="border border-[#0055FF] bg-[#F0F7FF] p-4 mb-5" data-testid="booking-checks-explainer">
      <div className="flex items-start gap-3">
        <Info size={20} weight="fill" className="text-[#0055FF] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-black tracking-tight text-[#0A0A0A] uppercase tracking-[0.16em]">
            How "Did they book?" works
          </h2>
          <ol className="mt-2 space-y-1.5 text-xs text-[#525252] list-decimal pl-4">
            <li>
              <strong className="text-[#0A0A0A]">Lead clicks the meeting link</strong> in
              one of our drip emails (e.g. the Zoom-scheduler "Book a 20 min call" CTA).
            </li>
            <li>
              <strong className="text-[#0A0A0A]">We pause their drip campaign immediately</strong> so
              they don't get pestered while they're (hopefully) scheduling.
              The exact step they were on is saved.
            </li>
            <li>
              <strong className="text-[#0A0A0A]">3 days later</strong>, the prompt below
              surfaces and an in-app notification pings the lead's assigned owner: <em>"Did they
              book?"</em>
            </li>
            <li>
              <strong className="text-[#15803D]">Yes → keep them out of the drip.</strong> We
              set their <code className="text-[10px] bg-white px-1 py-0.5 border border-[#E5E5E5]">demo_booked_date</code> so
              the engagement-follow-up engine also stops nudging them.
            </li>
            <li>
              <strong className="text-[#D97706]">No → resume the drip</strong> at the
              exact step they paused at — they pick up the cadence as if the click never
              happened.
            </li>
            <li>
              <strong className="text-[#A3A3A3]">If no decision is made</strong> after another
              3 days, the system <strong>auto-resumes</strong> the drip — default-safe behaviour
              so a forgotten lead never goes cold by accident.
            </li>
          </ol>
          <p className="text-[10px] text-[#A3A3A3] mt-3">
            You can also answer these prompts directly from the blue banner on the lead's detail
            page. <Link to="/crm/leads" className="text-[#0055FF] hover:underline">Open Sales Leads →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function BookingCheckInsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [mineOnly, setMineOnly] = useState(false);
  const [busyCheckId, setBusyCheckId] = useState(null);
  const [reopening, setReopening] = useState(false);

  // Session 82 follow-up — confirm-before-send modal. We load a live
  // preview from the backend (real lead data, real template
  // substitution, real branded HTML wrapper) so Mike can verify
  // recipient / subject / body before the send is committed.
  const [previewState, setPreviewState] = useState({
    open: false,
    loading: false,
    sending: false,
    checkId: null,
    data: null,
    error: null,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/crm/booking-checks?status=${status}&limit=200&mine_only=${mineOnly}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load booking check-ins");
    }
    setLoading(false);
  }, [status, mineOnly]);

  useEffect(() => { load(); }, [load]);

  const decide = async (checkId, decision) => {
    const notes = window.prompt(
      decision === "booked"
        ? "Optional note — when did they schedule the meeting?"
        : "Optional note — what did they say / why aren't they booked?",
      ""
    );
    if (notes === null) return; // cancelled
    setBusyCheckId(checkId);
    try {
      await api.post(
        `/crm/booking-checks/${checkId}/${decision === "booked" ? "booked" : "not-booked"}`,
        { notes }
      );
      toast.success(
        decision === "booked"
          ? "Booked — drip stays paused, demo date set."
          : "Resumed — drip continues from the same step."
      );
      // Tell the sidebar to refresh its "Booking Check-ins" badge
      // immediately instead of waiting up to 60s for the next poll.
      // Mike's report (May 28 2026): the count stayed at 5 after
      // every action because the sidebar only repolled on a timer.
      try {
        window.dispatchEvent(new CustomEvent("eyvo:booking-checks-changed"));
      } catch { /* IE fallback not relevant — production runs Chrome/Safari/Firefox */ }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decision failed");
    }
    setBusyCheckId(null);
  };

  // Session 81 follow-up — Mike asked for a third "Send Inquiry Email"
  // option that fires the operator-curated follow-up email and parks
  // the check as `inquiry_sent`. Template lives in System Admin →
  // Booking Inquiry Email so he can edit the copy without a deploy.
  // Session 82 follow-up — Mike's ask: clicking "Send Inquiry Email"
  // should NOT fire the email immediately. It should preview the
  // recipient address, subject, and rendered HTML body in a modal so
  // he can verify how the email will look before committing.
  //
  // Session 82 v1.46 — clicking Send commits the dispatch AND moves
  // the check out of Pending (status → not_booked, drip resumed,
  // lead promoted to hot, event logged). Frontend just shows the
  // toast + reloads the list; the sidebar badge auto-decrements via
  // the `eyvo:booking-checks-changed` event below.
  const sendInquiry = async (checkId) => {
    setPreviewState({
      open: true, loading: true, sending: false,
      checkId, data: null, error: null,
    });
    try {
      const r = await api.get(`/crm/booking-checks/${checkId}/inquiry-preview`);
      setPreviewState(prev => ({
        ...prev, loading: false, data: r.data, error: null,
      }));
    } catch (e) {
      setPreviewState(prev => ({
        ...prev, loading: false,
        error: e.response?.data?.detail || "Preview failed",
      }));
    }
  };

  const confirmSendInquiry = async () => {
    const checkId = previewState.checkId;
    if (!checkId) return;
    setPreviewState(prev => ({ ...prev, sending: true }));
    try {
      const r = await api.post(
        `/crm/booking-checks/${checkId}/send-inquiry`,
        { notes: "" }
      );
      toast.success(`Inquiry sent to ${r.data?.to_email || "lead"}.`);
      try { window.dispatchEvent(new CustomEvent("eyvo:booking-checks-changed")); }
      catch { /* noop */ }
      setPreviewState({
        open: false, loading: false, sending: false,
        checkId: null, data: null, error: null,
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Inquiry send failed");
      setPreviewState(prev => ({ ...prev, sending: false }));
    }
  };

  const closePreview = () => {
    if (previewState.sending) return; // don't allow close mid-send
    setPreviewState({
      open: false, loading: false, sending: false,
      checkId: null, data: null, error: null,
    });
  };

  // Bulk re-open every previously NO-resumed check so the operator
  // can hit the new "Send Inquiry" button on them. Mike's explicit
  // ask: "Can you retrieve all the booking check-ins I said no to
  // before, put them back in the booking check-ins list, and let me
  // click this button?"
  const reopenResolved = async () => {
    const ok = window.confirm(
      "Move every previously NO-resumed or auto-resumed booking check-in " +
      "back to Pending so you can fire the new inquiry email on them?\n\n" +
      "This does NOT touch the underlying drip — those leads kept " +
      "dripping after the original decision; this is purely UI re-surfacing."
    );
    if (!ok) return;
    setReopening(true);
    try {
      const r = await api.post("/crm/booking-checks/reopen-resolved");
      toast.success(
        `Re-opened ${r.data?.reopened || 0} previously-resolved check-in(s).`
      );
      try { window.dispatchEvent(new CustomEvent("eyvo:booking-checks-changed")); }
      catch { /* noop */ }
      setStatus("pending");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reopen failed");
    }
    setReopening(false);
  };

  return (
    <div className="p-6" data-testid="booking-checks-page">
      <div className="flex items-center gap-3 mb-1">
        <CalendarCheck size={22} className="text-[#0A0A0A]" />
        <h1 className="text-2xl font-black text-[#0A0A0A] tracking-tight">Booking Check-ins</h1>
      </div>
      <p className="text-xs text-[#A3A3A3] mb-4">
        Leads who clicked the Zoom-scheduler link — confirm whether they actually scheduled.
      </p>

      <HowItWorksCard />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex border border-[#E5E5E5] bg-white">
          {STATUS_TABS.map((t) => {
            const active = status === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setStatus(t.id)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                  active
                    ? "bg-[#0055FF] text-white"
                    : "text-[#525252] hover:bg-[#F5F5F5]"
                }`}
                data-testid={`booking-checks-tab-${t.id}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <label className="inline-flex items-center gap-2 text-xs text-[#525252]" data-testid="mine-only-toggle">
          <input
            type="checkbox"
            checked={mineOnly}
            onChange={(e) => setMineOnly(e.target.checked)}
            className="accent-[#0055FF]"
          />
          <span className="uppercase font-bold tracking-wider text-[10px]">Mine only</span>
        </label>

        <button
          onClick={reopenResolved}
          disabled={reopening}
          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#E5E5E5] text-[#525252] text-[10px] font-bold uppercase tracking-wider hover:border-[#0055FF] hover:text-[#0055FF] disabled:opacity-50"
          data-testid="booking-checks-reopen-resolved"
          title="Re-open every previously NO-resumed / auto-resumed check-in back to Pending"
        >
          <ArrowCounterClockwise size={11} weight="bold" />
          {reopening ? "Re-opening…" : "Reopen past 'No' decisions"}
        </button>

        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#E5E5E5] text-[#525252] text-[10px] font-bold uppercase tracking-wider hover:border-[#0055FF] hover:text-[#0055FF]"
          data-testid="booking-checks-refresh"
        >
          <ArrowsClockwise size={11} weight="bold" /> Refresh
        </button>
      </div>

      {loading && (
        <div className="text-sm text-[#A3A3A3] p-6 text-center" data-testid="booking-checks-loading">
          Loading…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="border border-dashed border-[#E5E5E5] p-10 text-center" data-testid="booking-checks-empty">
          <CalendarCheck size={28} className="text-[#A3A3A3] mx-auto mb-2" />
          <p className="text-sm font-bold text-[#525252]">
            No {status === "pending" ? "pending"
              : status === "auto_resumed" ? "auto-resumed"
              : status === "inquiry_sent" ? "inquiry-sent"
              : status} check-ins.
          </p>
          <p className="text-xs text-[#A3A3A3] mt-1">
            {status === "pending"
              ? "Looks like every meeting-link click has been confirmed."
              : status === "inquiry_sent"
              ? "No operator inquiry emails have been fired yet."
              : "Nothing here yet."}
          </p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="border border-[#E5E5E5] bg-white divide-y divide-[#F5F5F5]" data-testid="booking-checks-list">          {items.map((it) => {
            const badge = STATUS_BADGE[it.status] || STATUS_BADGE.pending;
            const isPending = it.status === "pending";
            const inFlight = busyCheckId === it.check_id;
            return (
              <div
                key={it.check_id}
                className="p-4 hover:bg-[#FAFAFA]"
                data-testid={`booking-check-row-${it.check_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/crm/leads/${it.lead_id}`}
                        className="text-sm font-black text-[#0A0A0A] tracking-tight hover:text-[#0055FF]"
                      >
                        {it.company_name || "(unknown company)"}
                      </Link>
                      <span className={`px-2 py-0.5 text-[10px] font-bold border ${badge.cls} uppercase tracking-wider`}>
                        {badge.label}
                      </span>
                      {it.workflow_name && (
                        <span className="text-[10px] text-[#A3A3A3]">
                          from "{it.workflow_name}"
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-[#525252] flex items-center gap-2 flex-wrap">
                      <span>{it.contact_person || it.contact_email}</span>
                      {it.contact_email && it.contact_person && (
                        <span className="text-[#A3A3A3]">·</span>
                      )}
                      {it.contact_email && (
                        <span className="text-[#A3A3A3]">{it.contact_email}</span>
                      )}
                    </div>
                    <div className="mt-2 text-[11px] text-[#525252] space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <Clock size={11} className="text-[#A3A3A3]" />
                        <span>
                          Clicked: <strong>{it.click_at ? formatLocal(it.click_at) : "—"}</strong>
                        </span>
                      </div>
                      {isPending && (
                        <div className="flex items-center gap-1.5">
                          <Clock size={11} className="text-[#A3A3A3]" />
                          <span>
                            Auto-resume if no decision by:{" "}
                            <strong>{it.auto_resume_at ? formatLocal(it.auto_resume_at) : "—"}</strong>
                          </span>
                        </div>
                      )}
                      {!isPending && it.decided_at && (
                        <div className="flex items-center gap-1.5">
                          <CheckCircle size={11} className="text-[#A3A3A3]" />
                          <span>
                            Decided by <strong>{it.decided_by_name}</strong>{" "}
                            on {formatLocal(it.decided_at)}
                          </span>
                        </div>
                      )}
                      {it.inquiry_sent_at && (
                        <div className="flex items-center gap-1.5">
                          <Envelope size={11} className="text-[#0055FF]" weight="fill" />
                          <span>
                            Inquiry email sent{" "}
                            {it.inquiry_sent_by_name ? `by ${it.inquiry_sent_by_name}` : ""}{" "}
                            on <strong>{formatLocal(it.inquiry_sent_at)}</strong>
                          </span>
                        </div>
                      )}
                      <div className="text-[10px] text-[#A3A3A3]">
                        Paused at step {it.paused_step_index}
                        {it.click_url && (
                          <>
                            {" · "}
                            <span className="truncate inline-block max-w-md align-bottom" title={it.click_url}>
                              link: {it.click_url}
                            </span>
                          </>
                        )}
                      </div>
                      {it.decision_notes && (
                        <div className="mt-1 text-[11px] text-[#525252] italic break-words">
                          "{it.decision_notes}"
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {isPending && (
                      <>
                        <button
                          onClick={() => decide(it.check_id, "booked")}
                          disabled={inFlight}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#15803D] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#166534] disabled:opacity-50 whitespace-nowrap"
                          data-testid={`booking-check-row-yes-${it.check_id}`}
                        >
                          <CheckCircle size={12} weight="bold" /> Yes — booked
                        </button>
                        <button
                          onClick={() => decide(it.check_id, "not_booked")}
                          disabled={inFlight}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#D97706] text-[#D97706] text-xs font-bold uppercase tracking-wider hover:bg-[#FFFBEB] disabled:opacity-50 whitespace-nowrap"
                          data-testid={`booking-check-row-no-${it.check_id}`}
                        >
                          <X size={12} weight="bold" /> No — resume
                        </button>
                      </>
                    )}
                    {!it.inquiry_sent_at && (
                      <button
                        onClick={() => sendInquiry(it.check_id)}
                        disabled={inFlight || previewState.loading || previewState.sending}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#0055FF] text-[#0055FF] text-xs font-bold uppercase tracking-wider hover:bg-[#EFF6FF] disabled:opacity-50 whitespace-nowrap"
                        data-testid={`booking-check-row-inquiry-${it.check_id}`}
                        title="Preview the operator-curated Discovery Call inquiry email before sending"
                      >
                        <Envelope size={12} weight="bold" /> Send Inquiry Email
                      </button>
                    )}
                    {it.inquiry_sent_at && (
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#EFF6FF] text-[#0055FF] text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
                        data-testid={`booking-check-row-inquiry-sent-${it.check_id}`}
                      >
                        <Envelope size={11} weight="fill" /> Inquiry sent
                      </span>
                    )}
                    <Link
                      to={`/crm/leads/${it.lead_id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E5E5] text-[#525252] text-[10px] font-bold uppercase tracking-wider hover:border-[#0055FF] hover:text-[#0055FF] whitespace-nowrap"
                      data-testid={`booking-check-row-open-${it.check_id}`}
                    >
                      Open lead <ArrowRight size={11} weight="bold" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirm-before-send preview modal — shows the exact recipient,
          From, subject, and rendered HTML body before committing the
          send. Mike's explicit ask: "It should preview the email to the
          screen exactly what it's going to send before it sends it." */}
      <Dialog open={previewState.open} onOpenChange={(v) => { if (!v) closePreview(); }}>
        <DialogContent
          className="max-w-3xl rounded-none border-[#E5E5E5]"
          data-testid="inquiry-preview-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-black tracking-tight text-[#0A0A0A] flex items-center gap-2">
              <Envelope size={20} weight="bold" />
              Preview inquiry email
            </DialogTitle>
            <p className="text-xs text-[#525252] mt-1">
              Review the email below. Click <strong>Send now</strong> to commit, or
              <strong> Cancel</strong> to back out — nothing is sent until you confirm.
            </p>
            <p className="text-[10px] text-[#A3A3A3] mt-1.5 leading-snug">
              On send, the check exits the Pending Decision queue, the lead&apos;s drip
              resumes from where it paused, the lead is restored to <strong>hot</strong>{" "}
              status (if it wasn&apos;t already), and a full entry is written to the lead&apos;s
              event log.
            </p>
          </DialogHeader>

          {previewState.loading && (
            <div className="py-10 flex justify-center" data-testid="inquiry-preview-loading">
              <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent animate-spin" />
            </div>
          )}

          {previewState.error && (
            <div className="bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] p-3 text-xs" data-testid="inquiry-preview-error">
              {previewState.error}
            </div>
          )}

          {previewState.data && !previewState.loading && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="inquiry-preview-body">
              <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-3 text-xs space-y-1.5 font-mono">
                <div data-testid="inquiry-preview-to">
                  <span className="font-bold text-[#525252] uppercase tracking-wider text-[10px] mr-2">To:</span>
                  <span className="text-[#0A0A0A]">
                    {previewState.data.to_name ? `${previewState.data.to_name} <${previewState.data.to_email}>` : previewState.data.to_email}
                  </span>
                </div>
                <div data-testid="inquiry-preview-from">
                  <span className="font-bold text-[#525252] uppercase tracking-wider text-[10px] mr-2">From:</span>
                  <span className="text-[#0A0A0A]">
                    {previewState.data.from_name} &lt;{previewState.data.from_email}&gt;
                  </span>
                </div>
                <div data-testid="inquiry-preview-reply-to">
                  <span className="font-bold text-[#525252] uppercase tracking-wider text-[10px] mr-2">Reply-To:</span>
                  <span className="text-[#0A0A0A]">{previewState.data.from_email}</span>
                </div>
                <div data-testid="inquiry-preview-subject">
                  <span className="font-bold text-[#525252] uppercase tracking-wider text-[10px] mr-2">Subject:</span>
                  <span className="text-[#0A0A0A] font-bold">{previewState.data.subject}</span>
                </div>
              </div>

              <div className="border border-[#E5E5E5] bg-white p-5" data-testid="inquiry-preview-html">
                <SafeHtml
                  html={previewState.data.body_html || ""}
                  className="text-sm text-[#0A0A0A] leading-relaxed"
                />
                <div className="mt-4 pt-3 border-t border-[#F5F5F5] text-[10px] text-[#A3A3A3] italic">
                  Note: the production email also carries the branded Eyvo logo header + footer
                  from <code className="bg-[#F5F5F5] px-1">shared.send_eyvo_email</code>. The
                  preview above shows the operator-edited body only.
                </div>
              </div>

              {previewState.data.already_sent && (
                <div className="bg-[#FFFBEB] border border-[#FDE68A] text-[#92400E] p-3 text-xs" data-testid="inquiry-preview-already-sent">
                  ⚠ This inquiry has already been sent on{" "}
                  <strong>{formatLocal(previewState.data.inquiry_sent_at)}</strong>. Re-sending is blocked.
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex items-center gap-2 mt-2">
            <Button
              type="button"
              onClick={closePreview}
              disabled={previewState.sending}
              variant="outline"
              className="rounded-none border-[#E5E5E5] text-xs font-bold uppercase tracking-wider"
              data-testid="inquiry-preview-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmSendInquiry}
              disabled={
                previewState.loading
                || previewState.sending
                || !previewState.data
                || previewState.data?.already_sent
                || !previewState.data?.to_email
              }
              className="bg-[#0055FF] hover:bg-[#003ECB] text-white text-xs font-bold uppercase tracking-wider rounded-none"
              data-testid="inquiry-preview-confirm"
            >
              <PaperPlaneTilt size={12} weight="bold" className="mr-1.5" />
              {previewState.sending ? "Sending…" : "Send now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
